import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { PeopleIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface PersonFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface MemoryCollection {
  personId: string;
  memories: string;
  files: AppDocument[];
}

// Memory templates for different relationships
const MEMORY_TEMPLATES = {
  spouse: (name: string) => `My dearest ${name}, where do I even begin to express what you have meant to me throughout our journey together? You have been my anchor, my confidant, and my greatest love. I remember the way your eyes lit up when we first met, and how that same sparkle never faded, even through our most challenging times. You taught me that love isn't just about the grand gestures, but about the quiet moments we shared over morning coffee, the way you held my hand during difficult times, and how you believed in me even when I didn't believe in myself. Your laughter filled our home with warmth, and your wisdom guided our family through every season of life. I want you to know that every day with you was a gift, and the love we built together will live on in the hearts of everyone whose lives you touched. Thank you for being my partner, my best friend, and the love of my life.`,

  child: (name: string) => `My precious ${name}, from the moment you came into this world, you changed everything. Watching you grow from that tiny bundle of joy into the incredible person you've become has been the greatest privilege of my life. I remember your first steps, your first words, and how your curiosity about the world never ceased to amaze me. You have brought so much light and laughter into our family, and your unique spirit has touched everyone around you. I'm so proud of your kindness, your determination, and the way you've always stayed true to yourself. As you continue on your journey, I want you to know that you are loved beyond measure, and that I believe in you completely. The world is a better place because you're in it, and I am forever grateful to be your parent. Remember that no matter where life takes you, you carry with you all the love and hopes I have for your happiness.`,

  parent: (name: string) => `Dear ${name}, you were the foundation upon which my entire life was built. Your love, guidance, and unwavering support shaped me into the person I am today. I remember the countless sacrifices you made for our family, often putting your own needs aside to ensure we had everything we needed. Your wisdom echoed through every important decision I made, and your values became the compass that guided my life. The bedtime stories, the encouraging words before big moments, the way you celebrated my victories and comforted me through defeats – these memories are treasures I carry with me always. You taught me what it means to love unconditionally, to work hard, and to treat others with respect and kindness. Even now, I find myself asking what you would do in difficult situations, because your example continues to light my way. Thank you for being not just my parent, but my first teacher, my biggest supporter, and one of my dearest friends.`,

  sibling: (name: string) => `${name}, my dear brother or sister, you've been my companion through every chapter of life. From our childhood adventures and shared secrets to navigating the complexities of adulthood together, you've been a constant source of strength and joy. I cherish the memories of our late-night conversations, our inside jokes that no one else understood, and the way we could communicate with just a look across a crowded room. You've seen me at my best and worst, and loved me through it all. Your unique perspective on life has challenged me to grow, and your unwavering loyalty has been a comfort I could always count on. We may have taken different paths, but the bond we share as siblings is unbreakable. Thank you for being my built-in best friend, my partner in mischief, and the person who knows my story better than anyone else. The memories we've created together are some of my most precious possessions.`,

  grandparent: (name: string) => `Beloved ${name}, you were the keeper of our family's stories and the source of unconditional love that every grandchild dreams of having. Your home was always a sanctuary filled with warmth, delicious smells from the kitchen, and the kind of patience that only grandparents possess. I treasure the afternoons spent listening to your stories about the old days, learning family recipes that carried generations of love, and feeling completely accepted just as I was. You had a special way of making each grandchild feel like they were your favorite, and your gentle wisdom helped shape my understanding of what truly matters in life. Your hugs could heal any hurt, and your encouragement gave me confidence to chase my dreams. The traditions you created, the values you instilled, and the love you shared continue to ripple through our family. Thank you for being our family's heart, our connection to our roots, and a shining example of grace and resilience.`,

  friend: (name: string) => `Dear ${name}, friendship like ours is a rare and precious gift. You've been my confidant, my cheerleader, and sometimes the voice of reason when I needed it most. Through all of life's ups and downs, you've stood by me with unwavering loyalty and genuine care. I remember the countless hours we spent talking about everything and nothing, the adventures we embarked on together, and how you always knew exactly what to say to make me feel better. Your friendship has enriched my life in ways I can't even begin to describe. You've celebrated my victories as if they were your own and supported me through challenges with endless patience and understanding. The laughter we've shared, the memories we've created, and the trust we've built mean everything to me. Thank you for being the kind of friend who makes life brighter simply by being in it, and for showing me what true friendship really looks like.`
};

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse (Husband/Wife)' },
  { value: 'child', label: 'Child (Son/Daughter)' },
  { value: 'parent', label: 'Parent (Mother/Father)' },
  { value: 'sibling', label: 'Sibling (Brother/Sister)' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'friend', label: 'Friend' }
];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame172: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask, isLoading } = useSessionPersistence();

  const [people, setPeople] = useState<UnifiedContact[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [personForm, setPersonForm] = useState<PersonFormData>({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [memoryCollections, setMemoryCollections] = useState<MemoryCollection[]>([]);
  const [currentMemories, setCurrentMemories] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<AppDocument[]>([]);

  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState<string>('');

  const [, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'unsaved'>('saved');

  const [isDragOver, setIsDragOver] = useState(false);

  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  const hasMeaningfulData = (collections: MemoryCollection[]) => {
    return collections.some(collection => {
      const hasText = collection.memories.trim().length > 0;
      const hasFiles = collection.files.length > 0;
      return hasText || hasFiles;
    });
  };

  useEffect(() => {
    if (isLoading) return;

    const loadData = async () => {
      console.log('[Frame172] Loading memory collections data...');

      try {
        const contacts = await unifiedContactService.getAllContacts();
        setPeople(contacts);
        console.log(`[Frame172] Loaded ${contacts.length} unified contacts`);
      } catch (error) {
        console.error('[Frame172] Error loading contacts:', error);
      }

      const localData = session.taskProgress?.memoryCollections;
      console.log('[Frame172] Local session data:', localData);

      let dataToLoad = null;

      if (localData) {
        console.log('[Frame172] Using local session data');
        dataToLoad = localData.answers ? localData.answers : localData;
      }

      if (dataToLoad) {
        console.log('[Frame172] Loading data:', dataToLoad);

        if (dataToLoad.memoryCollections) {
          const collections = dataToLoad.memoryCollections.map((collection: unknown) => ({
            ...collection,
            files: normalizeDocuments(collection.files || [])
          }));
          setMemoryCollections(collections);
          console.log('[Frame172] Loaded memory collections:', collections);

          if (hasMeaningfulData(collections)) {
            console.log('[Frame172] Meaningful data found on load, marking memoryCollections task as complete');
            completeTask('memoryCollections');
          }
        }
      } else {
        console.log('[Frame172] No data found - starting fresh');
      }
    };

    loadData();
  }, [isLoading, session.taskProgress?.memoryCollections]);

  useEffect(() => {
    if (!selectedPersonId) return;

    setSaveStatus('unsaved');

    const autoSaveTimer = setTimeout(async () => {
      await saveMemoryData();
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [currentMemories, selectedPersonId]);

  useEffect(() => {
    if (!selectedPersonId) return;

    setSaveStatus('unsaved');

    const autoSaveTimer = setTimeout(async () => {
      await saveMemoryData();
    }, 1000);

    return () => clearTimeout(autoSaveTimer);
  }, [uploadedFiles, selectedPersonId]);

  const saveToSession = (collections: MemoryCollection[]) => {
    console.log('[Frame172] saveToSession called with:', collections);

    const dataToSave = {
      memoryCollections: collections,
      lastUpdated: new Date().toISOString()
    };

    console.log('[Frame172] Saving to session:', dataToSave);
    updateTaskProgress('memoryCollections', dataToSave);
  };

  const saveMemoryData = async () => {
    if (!selectedPersonId) return;

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      const existingIndex = memoryCollections.findIndex(
        collection => collection.personId === selectedPersonId
      );

      const updatedCollection: MemoryCollection = {
        personId: selectedPersonId,
        memories: currentMemories,
        files: uploadedFiles
      };

      let updatedCollections: MemoryCollection[];
      if (existingIndex >= 0) {
        updatedCollections = [...memoryCollections];
        updatedCollections[existingIndex] = updatedCollection;
      } else {
        updatedCollections = [...memoryCollections, updatedCollection];
      }

      setMemoryCollections(updatedCollections);
      saveToSession(updatedCollections);

      if (hasMeaningfulData(updatedCollections)) {
        console.log('[Frame172] Meaningful data found, marking memoryCollections task as complete');
        completeTask('memoryCollections');
      }

      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('[Frame172] Error saving memory data:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const getFileTypeIcon = (mimeType?: string) => {
    const fileType = mimeType || '';
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('doc')) return '📝';
    if (fileType.includes('txt')) return '📄';
    return '📁';
  };

  const handleTemplateSelect = (relationship: string) => {
    const selectedPerson = getSelectedPerson();
    if (selectedPerson) {
      const template = MEMORY_TEMPLATES[relationship as keyof typeof MEMORY_TEMPLATES];
      if (template) {
        setCurrentMemories(template(getPersonName(selectedPerson)));
      }
    }
    setShowTemplateDropdown(false);
  };

  const handleAddPerson = () => {
    setShowAddPersonModal(true);
  };

  const handlePersonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await unifiedContactService.createContact({
        firstName: personForm.firstName.trim(),
        lastName: personForm.lastName.trim(),
        email: personForm.email.trim(),
        relationship: selectedRelationship || 'friend',
        source: 'memory',
        notes: 'Added for memory collection'
      });

      const contacts = await unifiedContactService.getAllContacts();
      setPeople(contacts);
      console.log(`[Frame172] Added new contact, loaded ${contacts.length} unified contacts`);

      if (hasMeaningfulData(memoryCollections)) {
        console.log('[Frame172] Meaningful data found after adding person, marking memoryCollections task as complete');
        completeTask('memoryCollections');
      }

      setPersonForm({ firstName: '', lastName: '', email: '' });
      setSelectedRelationship('');
      setShowAddPersonModal(false);
    } catch (error: unknown) {
      if (error.code === 'DUPLICATE_CONTACT') {
        alert(`A contact with email ${personForm.email} already exists. Please select them from the list.`);
      } else {
        alert('Failed to add contact. Please try again.');
        console.error('[Frame172] Error adding contact:', error);
      }
    }
  };

  const handlePersonSelect = (personId: string) => {
    setSelectedPersonId(personId);

    const existingCollection = memoryCollections.find(
      collection => collection.personId === personId
    );

    if (existingCollection) {
      setCurrentMemories(existingCollection.memories);
      setUploadedFiles(normalizeDocuments(existingCollection.files));
    } else {
      setCurrentMemories('');
      setUploadedFiles([]);
    }

    setShowMemoryModal(true);
  };

  const handleMemorySave = async () => {
    await saveMemoryData();
    setShowMemoryModal(false);
    setSelectedPersonId(null);
    setCurrentMemories('');
    setUploadedFiles([]);
    setShowTemplateDropdown(false);
    setIsDragOver(false);
  };

  const handleFormChange = (field: keyof PersonFormData, value: string) => {
    setPersonForm(prev => ({ ...prev, [field]: value }));
  };

  const getSelectedPerson = () => {
    return people.find(person => person.id === selectedPersonId);
  };

  const getPersonName = (person: UnifiedContact) => {
    return `${person.firstName} ${person.lastName}`;
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame172] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleDeletePerson = async (personId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    console.log('[Frame172] Deleting person:', personId);

    try {
      const collection = memoryCollections.find(item => item.personId === personId);
      const files = normalizeDocuments(collection?.files || []);

      for (const file of files) {
        try {
          await documentService.delete(file.id);
        } catch (error) {
          console.error('[Frame172] Failed to delete document while deleting person collection:', error);
        }
      }

      await unifiedContactService.deleteContact(personId);

      const contacts = await unifiedContactService.getAllContacts();
      setPeople(contacts);

      const updatedCollections = memoryCollections.filter(collection => collection.personId !== personId);
      setMemoryCollections(updatedCollections);

      if (selectedPersonId === personId) {
        setSelectedPersonId(null);
        setCurrentMemories('');
        setUploadedFiles([]);
        setShowMemoryModal(false);
      }

      saveToSession(updatedCollections);

      if (hasMeaningfulData(updatedCollections)) {
        console.log('[Frame172] Still have meaningful data after deletion, keeping memoryCollections task as complete');
        completeTask('memoryCollections');
      }

      console.log('[Frame172] Person deleted successfully');
    } catch (error) {
      console.error('[Frame172] Error deleting person:', error);
      alert('Failed to delete contact. Please try again.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setSaveStatus('saving');

    const uploadedFilesList = await Promise.all(
      files.map(async (file) => documentService.upload(file))
    );

    const successfulUploads = uploadedFilesList.filter((f): f is AppDocument => f !== null);

    if (successfulUploads.length > 0) {
      setUploadedFiles(prev => [...prev, ...successfulUploads]);
      setSaveStatus('saved');
      setLastSaved(new Date());
    }

    if (successfulUploads.length < files.length) {
      setSaveStatus('error');
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setSaveStatus('saving');

    const uploadedFilesList = await Promise.all(
      files.map(async (file) => documentService.upload(file))
    );

    const successfulUploads = uploadedFilesList.filter((f): f is AppDocument => f !== null);

    if (successfulUploads.length > 0) {
      setUploadedFiles(prev => [...prev, ...successfulUploads]);
      setSaveStatus('saved');
      setLastSaved(new Date());
    }

    if (successfulUploads.length < files.length) {
      setSaveStatus('error');
    }

    e.target.value = '';
  };

  const removeFile = async (id: string) => {
    const fileToRemove = uploadedFiles.find(item => item.id === id);
    if (!fileToRemove) return;

    try {
      await documentService.delete(fileToRemove.id);
    } catch (error) {
      console.error('[Frame172] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setUploadedFiles(prev => prev.filter(item => item.id !== id));
    setSaveStatus('unsaved');
  };

  const handleRemoveSavedFile = async (personId: string, fileId: string) => {
    try {
      await documentService.delete(fileId);

      const updatedCollections = memoryCollections.map((collection) => {
        if (collection.personId !== personId) return collection;

        return {
          ...collection,
          files: collection.files.filter((file) => file.id !== fileId)
        };
      });

      setMemoryCollections(updatedCollections);
      saveToSession(updatedCollections);
    } catch (error) {
      console.error('[Frame172] Failed to remove file:', error);
      alert('Failed to remove file. Please try again.');
    }
  };

  const toggleExpandedCard = (personId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [personId]: !prev[personId]
    }));
  };

  const handleNext = () => {
    if (hasMeaningfulData(memoryCollections)) {
      console.log('[Frame172] Meaningful data found on navigation, marking memoryCollections task as complete');
      completeTask('memoryCollections');
    }
    navigate('/frame-173');
  };

  const handleBack = () => {
    if (hasMeaningfulData(memoryCollections)) {
      console.log('[Frame172] Meaningful data found on navigation, marking memoryCollections task as complete');
      completeTask('memoryCollections');
    }
    navigate('/frame-157');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader
          title="Memories for your loved ones"
        />

        <div className="flex-1 space-y-8">
          <div className="max-w-4xl">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              End of an Era is a perfect place to store photos, videos, and writing that you'd like shared with your loved ones after your death. Select or add a person to start crafting a personalized collection for them.
            </p>
          </div>

          <div className="space-y-4">
            {people.length > 0 && (
              <h2 className="text-stone-800 text-xl font-semibold font-['Work_Sans'] mb-4">
                Memories for Your Loved Ones ({people.length})
              </h2>
            )}

            <div className={`space-y-4 ${people.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}>
              {people.length > 0 ? (
                <div className="space-y-4">
                  {people.map((person) => {
                    const collection = memoryCollections.find(c => c.personId === person.id);
                    const hasMemories = !!collection;
                    const hasNotes = !!collection?.memories?.trim();
                    const hasFiles = (collection?.files?.length || 0) > 0;
                    const memoryPreview = collection?.memories
                      ? collection.memories.substring(0, 100) + (collection.memories.length > 100 ? '...' : '')
                      : 'No memories yet.';
                    const fileCount = collection?.files?.length || 0;
                    const isExpanded = !!expandedCards[person.id];

                    return (
                      <div
                        key={person.id}
                        className="bg-white rounded-lg border border-neutral-200 p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800">
                              {getPersonName(person)}
                            </div>
                            <div className="text-sm text-stone-500 mt-1">
                              {hasNotes && hasFiles
                                ? `Notes • ${fileCount} file(s)`
                                : hasNotes
                                  ? 'Notes'
                                  : hasFiles
                                    ? `${fileCount} file(s)`
                                    : 'No memories yet.'}
                            </div>

                            {!isExpanded && hasMemories && (
                              <div className="text-sm text-stone-600 mt-2">
                                {memoryPreview}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {hasMemories && (
                              <button
                                onClick={() => toggleExpandedCard(person.id)}
                                className="text-stone-600 hover:text-stone-800 px-3 py-1 rounded border border-stone-300 hover:border-stone-500"
                              >
                                {isExpanded ? 'Collapse' : 'View Note'}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePersonSelect(person.id);
                              }}
                              className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                            >
                              {hasMemories ? 'Edit' : 'Start'}
                            </button>
                            <button
                              onClick={(e) => handleDeletePerson(person.id, e)}
                              className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {isExpanded && collection && (
                          <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                            {hasNotes && (
                              <div>
                                <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                  Notes
                                </div>
                                <div className="text-sm text-stone-600 whitespace-pre-wrap leading-relaxed">
                                  {collection.memories}
                                </div>
                              </div>
                            )}

                            {hasFiles && (
                              <div>
                                <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                  Files
                                </div>
                                <div className="space-y-2">
                                  {collection.files.map((file) => (
                                    <div
                                      key={file.id}
                                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-md border border-stone-200"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => openDocument(file)}
                                        className="flex items-start gap-3 flex-1 min-w-0 hover:text-cyan-700 transition-colors text-left"
                                      >
                                        <span className="text-xl flex-shrink-0">{getFileTypeIcon(file.mimeType)}</span>
                                        <div className="min-w-0">
                                          <div className="font-sans text-sm font-medium text-stone-800 break-words whitespace-normal">
                                            {file.name}
                                          </div>
                                          {typeof file.size === 'number' && (
                                            <div className="font-sans text-xs text-stone-500">
                                              {(file.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                          )}
                                        </div>
                                      </button>

                                      <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                                        <button
                                          type="button"
                                          onClick={() => downloadDocument(file)}
                                          className="text-cyan-600 hover:text-cyan-800 text-xs"
                                          title="Download document"
                                        >
                                          Download
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => handleRemoveSavedFile(person.id, file.id)}
                                          className="text-xs text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                          title="Remove document"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No people added yet. Add a new person to start collecting memories.</p>
                </div>
              )}
            </div>

            <div className="text-base font-normal font-['Work_Sans']">
              <span className="text-stone-500">or </span>
              <button
                className="text-stone-500 underline cursor-pointer hover:text-stone-700"
                onClick={handleAddPerson}
              >
                add a new person.
              </button>
            </div>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Leave Behind Memories and Messages"
        subtitle="Choose who you want to leave memories for"
        progressPercent={50}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      {showAddPersonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-stone-800">Add a person</h2>
              <button
                onClick={() => setShowAddPersonModal(false)}
                className="text-stone-500 hover:text-stone-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handlePersonSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={personForm.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={personForm.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={personForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Relationship</label>
                <select
                  value={selectedRelationship}
                  onChange={(e) => setSelectedRelationship(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                >
                  <option value="">Select relationship (optional)</option>
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddPersonModal(false)}
                  className="flex-1 px-4 py-2 border border-neutral-300 text-stone-700 rounded-md hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800"
                >
                  Add Person
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMemoryModal && selectedPersonId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-lg relative flex flex-col">
            <div className="p-6 border-b border-neutral-200 flex-shrink-0">
              <h2 className="text-cyan-900 text-xl font-semibold font-['Test_Domaine_Display'] leading-relaxed">
                Your Legacy: Memories for {getPersonName(getSelectedPerson()!)}
              </h2>
              <div className="flex items-center gap-2 mt-2 text-stone-500 text-sm font-normal font-['Work_Sans']">
                {saveStatus === 'saving' && (
                  <>
                    <div className="w-3 h-3 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && lastSaved && (
                  <>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Auto-saved {lastSaved.toLocaleTimeString()}</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>Save failed</span>
                  </>
                )}
                {saveStatus === 'unsaved' && (
                  <>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span>Unsaved changes</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-full">
                <div className="space-y-4">
                  <div className="bg-white rounded border border-neutral-200 p-4 h-96 flex flex-col">
                    <div className="text-stone-800 text-sm font-normal font-['Test_Domaine_Display'] leading-none mb-3">
                      Share your memories
                    </div>
                    <textarea
                      value={currentMemories}
                      onChange={(e) => setCurrentMemories(e.target.value)}
                      className="flex-1 resize-none border-none outline-none text-stone-800 text-sm font-normal font-['Work_Sans'] p-0"
                      placeholder="Write about your memories, stories, or messages you'd like to share..."
                    />
                  </div>

                  <div className="relative">
                    <button
                      onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                      className="px-4 py-2 bg-cyan-700 text-white text-sm rounded-md hover:bg-cyan-800 flex items-center gap-2"
                    >
                      Use Template
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showTemplateDropdown && (
                      <div className="absolute bottom-full mb-2 left-0 w-64 bg-white border border-neutral-200 rounded-md shadow-lg z-10">
                        <div className="p-3 border-b border-neutral-100">
                          <div className="text-xs font-medium text-stone-600">Select Relationship:</div>
                        </div>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleTemplateSelect(option.value)}
                            className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      isDragOver ? 'border-cyan-500 bg-cyan-50' : 'border-neutral-300'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="text-stone-600 text-sm font-normal font-['Work_Sans'] mb-4">
                      Drag and drop files here, or click to select
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileInputChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="px-4 py-2 bg-neutral-100 text-stone-700 rounded-md cursor-pointer hover:bg-neutral-200 inline-block"
                    >
                      Choose Files
                    </label>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {uploadedFiles.map((fileItem) => (
                        <div
                          key={fileItem.id}
                          className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-neutral-50 rounded-md"
                        >
                          <div className="text-2xl">{getFileTypeIcon(fileItem.mimeType)}</div>

                          <button
                            type="button"
                            onClick={() => openDocument(fileItem)}
                            className="flex-1 min-w-0 text-left"
                          >
                            <div className="text-sm font-medium text-stone-800 break-words whitespace-normal">
                              {fileItem.name}
                            </div>
                            {typeof fileItem.size === 'number' && (
                              <div className="text-xs text-stone-500">
                                {(fileItem.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            )}
                          </button>

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => downloadDocument(fileItem)}
                              className="text-cyan-600 hover:text-cyan-800 text-xs"
                            >
                              Download
                            </button>

                            <button
                              onClick={() => removeFile(fileItem.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex-shrink-0">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowMemoryModal(false);
                    setSelectedPersonId(null);
                    setCurrentMemories('');
                    setUploadedFiles([]);
                    setShowTemplateDropdown(false);
                    setIsDragOver(false);
                  }}
                  className="px-6 py-2 border border-neutral-300 text-stone-700 rounded-md hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMemorySave}
                  className="px-6 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800"
                >
                  Save Memories
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResponsiveContainer>
  );
};

export default Frame172;